"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Widget anti-robot Cloudflare Turnstile (03/10/2026), pour les formulaires
 * `ContactSplit` et `LeadForm`. Le script de Cloudflare ne se charge que là
 * où le widget est rendu, c'est-à-dire sur les pages de formulaire, et
 * seulement si la page passe une clé de site.
 *
 * Rendu explicite plutôt qu'implicite : le widget se pose à chaque montage,
 * y compris quand on revient sur l'écran des coordonnées du `LeadForm`, ce
 * que le rendu implicite (un seul balayage au chargement du script) ne fait
 * pas. Cloudflare ajoute dans le conteneur un champ caché
 * `cf-turnstile-response` : posé à l'intérieur d'un <form>, le jeton part
 * avec le formulaire, en HTML classique comme en `FormData`. Le serveur le
 * vérifie (src/lib/email/form-handler.ts).
 *
 * `appearance: "interaction-only"` : rien ne s'affiche tant que Cloudflare
 * ne demande pas de clic, le formulaire garde son dessin.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Nom du champ caché que Cloudflare ajoute au formulaire. */
export const TURNSTILE_RESPONSE_FIELD = "cf-turnstile-response";

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string | null | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile")));
    script.onerror = () => {
      loading = null;
      reject(new Error("turnstile"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export type TurnstileProps = {
  /** Clé de site publique (NEXT_PUBLIC_TURNSTILE_SITE_KEY). */
  siteKey: string;
  /**
   * Pour un formulaire HTML classique : bloque l'envoi tant que le jeton
   * n'est pas arrivé, avec un message, au lieu d'un refus du serveur.
   */
  guardSubmit?: boolean;
  /** Incrémenter pour redemander un jeton (un jeton ne sert qu'une fois : après un envoi refusé). */
  resetKey?: number;
  className?: string;
};

export function Turnstile({ siteKey, guardSubmit = false, resetKey = 0, className }: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        if (cancelled || !ref.current) return;
        widgetId.current =
          api.render(ref.current, {
            sitekey: siteKey,
            language: "fr",
            theme: "light",
            appearance: "interaction-only",
            callback: () => setNotice(""),
          }) ?? null;
      })
      .catch(() => {
        if (!cancelled) setNotice("La vérification anti-robot n'a pas pu se charger. Écrivez-nous à contact@yellowplus.io.");
      });
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetKey > 0 && widgetId.current) window.turnstile?.reset(widgetId.current);
  }, [resetKey]);

  useEffect(() => {
    const form = guardSubmit ? ref.current?.closest("form") : null;
    if (!form) return;
    const onSubmit = (event: SubmitEvent): void => {
      const token = new FormData(form).get(TURNSTILE_RESPONSE_FIELD);
      if (typeof token === "string" && token) return;
      event.preventDefault();
      setNotice("Vérification anti-robot en cours. Réessayez dans un instant.");
    };
    form.addEventListener("submit", onSubmit);
    return () => form.removeEventListener("submit", onSubmit);
  }, [guardSubmit]);

  return (
    <div className={className} data-turnstile="true">
      <div ref={ref} />
      {/* Région annoncée toujours présente : un lecteur d'écran lit le message dès qu'il change. */}
      <p role="status" style={notice ? { margin: "8px 0 0", fontSize: 14 } : { margin: 0 }}>
        {notice}
      </p>
    </div>
  );
}
